import { useState } from "react";
import { motion } from "framer-motion";
import { Armchair, Wifi } from "lucide-react";
import { formatARS } from "../../lib/site";
import { EASE } from "./Motion";

const hm = (at) => at.slice(11, 16);
const mins = (a, b) => Math.round((new Date(b) - new Date(a)) / 60000);

// En ciudades con varias paradas (Buenos Aires: Virrey del Pino a Pacheco) el pasajero elige dónde sube y dónde baja.
const withStops = (trip, a, b) => {
  if (a === trip.from_idx && b === trip.to_idx) return trip;
  const up = trip.boardings?.find((x) => x.idx === a);
  const down = trip.alightings?.find((x) => x.idx === b);
  if (!up || !down) return trip;
  const m = mins(up.at, down.at);
  return {
    ...trip, from_idx: up.idx, from_stop: up.stop, departure: hm(up.at), to_idx: down.idx, to_stop: down.stop, arrival: hm(down.at),
    duration: `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`, arrival_next_day: down.at.slice(0, 10) > up.at.slice(0, 10),
  };
};

const StopSelect = ({ label, options, value, onChange, testid }) => (
  <label className="flex flex-col gap-1 text-xs text-[var(--muted-ink)]">
    {label}
    <select data-testid={testid} value={value} onChange={(e) => onChange(Number(e.target.value))} className="h-10 rounded-xl border border-black/10 bg-white px-3 text-sm font-semibold text-[var(--ink)]">
      {options.map((o) => <option key={o.idx} value={o.idx}>{o.stop} · {hm(o.at)}</option>)}
    </select>
  </label>
);

export const TripCard = ({ trip: base, index, onSelect, onWaitlist }) => {
  const [a, setA] = useState(base.from_idx);
  const [b, setB] = useState(base.to_idx);
  const trip = withStops(base, a, b);
  return (
  <motion.article
    data-testid={`trip-card-${index}`}
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.07, duration: 0.7, ease: EASE }}
    className="ticket-cut group grid grid-cols-1 overflow-hidden rounded-3xl bg-white ring-1 ring-black/5 transition-shadow duration-500 hover:shadow-[0_30px_60px_-30px_rgba(11,83,35,.45)] md:grid-cols-[1fr_auto]"
  >
    <div className="p-6 md:p-8">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-widest ${trip.service === "Semicama" ? "bg-[var(--paper)] text-[var(--ink)]" : "bg-[var(--forest)] text-white"}`}>{trip.service}</span>
        <span className="flex items-center gap-1 text-xs text-[var(--muted-ink)]"><Armchair className="h-3.5 w-3.5" /> {trip.from_stop} → {trip.to_stop}</span>
        <span className="flex items-center gap-1 text-xs text-[var(--muted-ink)]"><Wifi className="h-3.5 w-3.5" /> WiFi a bordo</span>
      </div>
      <div className="mt-6 flex items-center gap-4 md:gap-8">
        <div>
          <p className="font-display text-4xl md:text-5xl">{trip.departure}</p>
          <p className="mt-1 text-sm text-[var(--muted-ink)]">{trip.origin}</p>
        </div>
        <div className="flex flex-1 flex-col items-center">
          <span className="font-mono text-xs text-[var(--muted-ink)]">{trip.duration}</span>
          <div className="relative my-2 h-[2px] w-full bg-black/10">
            <span className="absolute left-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-[var(--forest)]" />
            <span className="absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-[var(--lime)] ring-2 ring-[var(--forest)]" />
            <span className="absolute inset-y-0 left-0 w-0 bg-[var(--forest)] transition-[width] duration-700 group-hover:w-full" />
          </div>
        </div>
        <div className="text-right">
          <p className="font-display text-4xl md:text-5xl">{trip.arrival}</p>
          <p className="mt-1 text-sm text-[var(--muted-ink)]">{trip.destination}{trip.arrival_next_day ? " · +1 día" : ""}</p>
        </div>
      </div>
      {(base.boardings?.length > 1 || base.alightings?.length > 1) && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {base.boardings?.length > 1 && <StopSelect label="Subís en" options={base.boardings} value={a} onChange={setA} testid={`trip-boarding-${index}`} />}
          {base.alightings?.length > 1 && <StopSelect label="Bajás en" options={base.alightings} value={b} onChange={setB} testid={`trip-alighting-${index}`} />}
        </div>
      )}
    </div>
    <div className="flex items-center justify-between gap-4 border-t border-dashed border-black/15 bg-[var(--paper)]/60 p-6 md:flex-col md:items-end md:justify-center md:border-l md:border-t-0 md:px-10">
      <div className="md:text-right">
        <p className="text-xs text-[var(--muted-ink)]">{trip.categories?.length > 1 ? "desde, por persona" : "por persona"}</p>
        {trip.promo > 0 && (
          <p data-testid={`trip-promo-${index}`} title="Promoción" className="mt-1 flex items-center gap-2 md:justify-end">
            <span className="rounded-full bg-[var(--ink)] px-2 py-0.5 text-[11px] font-bold text-[var(--lime)]">PROMO {trip.promo}% OFF</span>
            <s className="text-sm text-[var(--muted-ink)]">{formatARS(trip.base_price)}</s>
          </p>
        )}
        {trip.promo > 0 && trip.promo_left != null && (
          <p data-testid={`trip-promo-left-${index}`} className="mt-1 text-xs font-semibold text-[var(--forest)]">Quedan {trip.promo_left} {trip.promo_left === 1 ? "butaca" : "butacas"} en promo</p>
        )}
        <p data-testid={`trip-price-${index}`} className="font-display-up text-3xl text-[var(--forest)]">{formatARS(trip.price)}</p>
        {trip.categories?.length > 1 && (
          <div data-testid={`trip-categories-${index}`} className="mt-1 space-y-0.5 font-mono text-[11px] text-[var(--muted-ink)]">
            {trip.categories.map((c) => <p key={c.category}>{c.category} {formatARS(c.price)} · {c.free} libres</p>)}
          </div>
        )}
        <p className={`mt-1 font-mono text-[11px] ${trip.sold_out ? "font-bold text-red-700" : "text-[var(--muted-ink)]"}`}>{trip.sold_out ? (trip.seats_left ? `Solo ${trip.seats_left} libre(s)` : "Completo") : `${trip.seats_left} butacas libres`}</p>
      </div>
      {trip.sold_out ? (
        <button onClick={() => onWaitlist(trip)} data-testid={`trip-waitlist-${index}`} className="rounded-full border-2 border-[var(--ink)] px-5 py-2.5 text-sm font-bold transition-[background-color,color] duration-300 hover:bg-[var(--ink)] hover:text-white">
          Lista de espera
        </button>
      ) : (
      <button onClick={() => onSelect(trip)} data-testid={`trip-select-${index}`} className="rounded-full bg-[var(--ink)] px-6 py-3 text-sm font-bold text-white transition-[background-color,color,transform] duration-300 hover:bg-[var(--lime)] hover:text-[var(--ink)] active:scale-95">
        Elegir
      </button>
      )}
    </div>
  </motion.article>
  );
};
